import { type SqlString, sql } from "../SqlString";
import { type SqliteTableDef } from "../schema";
import { canonicalJsonString } from "../types/canonicalJsonString";

/**
 * Creates a SQL statement that upserts the given data into the given table.
 */
export function upsertSql(tableDef: SqliteTableDef, data: Record<string, unknown>): SqlString {
	const { tableName, columns } = tableDef;

	// Get the primary key column(s)
	const primaryKeyColumns = columns.filter((col) => col.primaryKey);
	if (primaryKeyColumns.length === 0) {
		throw new Error("Table must have at least one primary key column for UPSERT");
	}

	// Build the column names and values for the INSERT part
	const columnNames = columns.map((col) => sql.id(col.name));
	const values = columns.map((col): SqlString => {
		const value = data[col.name];
		if (value === undefined) {
			if (col.nullable === false && col.defaultValue === undefined) {
				throw new Error(`Required column ${col.name} has no value and no default`);
			}
			if (col.defaultValue === undefined || col.defaultValue === null) {
				return sql.lit("NULL");
			}
			if (typeof col.defaultValue === "string") {
				return sql.quote(col.defaultValue);
			}
			if (typeof col.defaultValue === "number" || typeof col.defaultValue === "boolean") {
				return sql.lit(col.defaultValue);
			}
			if (col.defaultValue instanceof Object && "statement" in col.defaultValue) {
				return col.defaultValue as SqlString;
			}
			throw new Error(
				`Unsupported default value type for column ${col.name}: ${typeof col.defaultValue}`
			);
		}
		// Convert value to appropriate SQL literal
		if (value === null) {
			return sql.lit("NULL");
		}
		if (typeof value === "string") {
			return sql.quote(value);
		}
		if (typeof value === "number" || typeof value === "boolean") {
			return sql.lit(value);
		}
		if (typeof value === "object" && value !== null) {
			return sql.quote(canonicalJsonString(value));
		}
		throw new Error(`Unsupported value type for column ${col.name}: ${typeof value}`);
	});

	return sql`INSERT OR REPLACE INTO ${sql.id(tableName)} (
	${sql.join(columnNames, ",\n	")}
) VALUES (
	${sql.join(values, ",\n	")}
)`;
}
